/**
 * Input/Output Sanitization Utilities
 * Prevents XSS and other injection attacks
 */

/**
 * Sanitize string input
 */
export const sanitizeString = (input) => {
    if (typeof input !== 'string') {
        return input;
    }
    
    // Remove null bytes
    let sanitized = input.replace(/\0/g, '');
    
    // Trim whitespace
    sanitized = sanitized.trim();
    
    // Remove potentially dangerous characters
    // Keep basic alphanumeric, spaces, and common punctuation
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove < and > to prevent HTML injection
    
    return sanitized;
};

/**
 * Sanitize object recursively
 */
export const sanitizeObject = (obj) => {
    if (obj === null || obj === undefined) {
        return obj;
    }
    
    if (typeof obj === 'string') {
        return sanitizeString(obj);
    }
    
    if (Array.isArray(obj)) {
        return obj.map(item => sanitizeObject(item));
    }
    
    if (typeof obj === 'object') {
        const sanitized = {};
        for (const [key, value] of Object.entries(obj)) {
            // Sanitize key
            const sanitizedKey = sanitizeString(key);
            // Sanitize value
            sanitized[sanitizedKey] = sanitizeObject(value);
        }
        return sanitized;
    }
    
    return obj;
};

/**
 * Sanitize user input before sending to API
 * Excludes password fields to preserve special characters
 */
export const sanitizeInput = (input, excludeFields = ['password']) => {
    if (!input || typeof input !== 'object') {
        return sanitizeObject(input);
    }
    
    // If it's an object, preserve excluded fields
    if (typeof input === 'object' && !Array.isArray(input)) {
        const sanitized = {};
        for (const [key, value] of Object.entries(input)) {
            // Don't sanitize excluded fields (like password)
            if (excludeFields.includes(key.toLowerCase())) {
                sanitized[key] = value; // Keep original value
            } else {
                sanitized[key] = sanitizeObject(value);
            }
        }
        return sanitized;
    }
    
    return sanitizeObject(input);
};

/**
 * Sanitize API response data
 */
export const sanitizeResponse = (response) => {
    if (response && response.data) {
        response.data = sanitizeObject(response.data);
    }
    return response;
};

/**
 * Remove sensitive fields from object
 */
export const removeSensitiveFields = (obj, sensitiveFields = ['password', 'token', 'secret', 'key', 'auth']) => {
    if (!obj || typeof obj !== 'object') {
        return obj;
    }
    
    if (Array.isArray(obj)) {
        return obj.map(item => removeSensitiveFields(item, sensitiveFields));
    }
    
    const sanitized = { ...obj };
    
    for (const key of Object.keys(sanitized)) {
        const lowerKey = key.toLowerCase();
        if (sensitiveFields.some(field => lowerKey.includes(field))) {
            delete sanitized[key];
        } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
            sanitized[key] = removeSensitiveFields(sanitized[key], sensitiveFields);
        }
    }
    
    return sanitized;
};

/**
 * Validate email format
 */
export const validateEmail = (email) => {
    if (!email || typeof email !== 'string') {
        return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
};

/**
 * Validate phone number format (basic)
 */
export const validatePhone = (phone) => {
    if (!phone || typeof phone !== 'string') {
        return false;
    }
    // Remove common formatting characters
    const cleaned = phone.replace(/[\s\-\(\)\+]/g, '');
    // Check if it's all digits and reasonable length
    return /^\d{10,15}$/.test(cleaned);
};

/**
 * Escape HTML to prevent XSS
 */
export const escapeHtml = (text) => {
    if (typeof text !== 'string') {
        return text;
    }
    
    const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    };
    
    return text.replace(/[&<>"']/g, (m) => map[m]);
};

export default {
    sanitizeString,
    sanitizeObject,
    sanitizeInput,
    sanitizeResponse,
    removeSensitiveFields,
    validateEmail,
    validatePhone,
    escapeHtml
};


